import {
  Container,
  GhostButton,
  Layout,
  PrimaryButton,
  ProgressLoader,
  SecondaryTopNav,
  Text,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { t } from "i18next";
import React, { PropsWithChildren, useContext, useState } from "react";

import { CreateGroupContext } from "./CreateGroupContext";
import { PageTitle } from "../../../../components/page-title/PageTitle";
import { ExitGroupCreateModal } from "../modals/ExitGroupCreateModal";

interface Props extends PropsWithChildren {
  title: string | React.JSX.Element;
  submitting?: boolean;
}

export function CreateGroupPageLayout(props: Props) {
  const { title, submitting, children } = props;
  const { goToPrevStep, currentStep, totalSteps } =
    useContext(CreateGroupContext);
  const [displayModal, setDisplayModal] = useState(false);

  const atLastStep = currentStep >= totalSteps - 1;
  const isFirstStep = currentStep === 0;
  const readableCurrentStep = currentStep + 1;

  return (
    <Layout
      layout="narrow"
      header={
        <SecondaryTopNav
          testID="close-button"
          leftIcon={isFirstStep ? "close" : "arrow_back"}
          onleftIconOnPress={
            isFirstStep ? () => setDisplayModal(true) : goToPrevStep
          }
        >
          {currentStep > 0 && (
            <GhostButton
              onPress={() => setDisplayModal(true)}
              size="medium"
              testID="previous-step-button"
            >
              {t("Cancel")}
            </GhostButton>
          )}

          <PrimaryButton
            type="submit"
            isLoading={submitting}
            testID={
              atLastStep
                ? "complete-group-button"
                : "create-group-next-step-button"
            }
          >
            {!atLastStep ? t("Next step") : t("Complete group")}
          </PrimaryButton>
        </SecondaryTopNav>
      }
    >
      <Container>
        {displayModal && (
          <ExitGroupCreateModal onClose={() => setDisplayModal(false)} />
        )}
        <div className={styles.container}>
          <div>
            <Text style="input/section-title">
              {t(`step {{readableCurrentStep}} of {{steps}}`, {
                readableCurrentStep,
                steps: totalSteps,
              })}
            </Text>
            <PageTitle>{title}</PageTitle>
          </div>
          <ProgressLoader floating value={(currentStep / totalSteps) * 100} />

          {children}
        </div>
      </Container>
    </Layout>
  );
}

const styles = {
  container: css({
    paddingLeft: "var(--space-8)",
    paddingRight: "var(--space-8)",
    paddingTop: "var(--space-7)",
    paddingBottom: "var(--space-7)",
    gap: "var(--space-3)",
    display: "flex",
    flexDirection: "column",
  }),
};
